import { isPlatformServer } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { GoogleEventTypes } from 'src/app/modules/shared/enum/datalayer-event-types';
import { Exchange } from 'src/app/modules/shared/models/exchange.model';
import { Product } from 'src/app/modules/shared/models/products.model';
import { DataLayerService } from 'src/app/modules/shared/services/data-layer/data-layer.service';
import { GoogleTagManagerService } from 'src/app/modules/shared/services/google-tag-manager.service';
import { PaymentService } from 'src/app/modules/shared/services/payment.service';
import { ExchangeResolverService } from 'src/app/modules/shared/services/resolver/exchange-resolver.service';
import { ProfileResolverService } from 'src/app/modules/shared/services/resolver/profile-resolver.service';
import { StoreDetailService } from 'src/app/modules/shared/services/resolver/store-detail.service';
import { LocalStorageService } from 'src/app/modules/shared/services/storage/local-storage.service';
import {
  OrderData,
  PaymentData,
  PriceSuggestion,
  VarietyAvailability
} from 'src/app/modules/shared/models/payment.model';
import { FACEBOOK_EVENT_TYPES } from 'src/app/modules/shared/services/meta-pixel/meta-pixel.model';
import { MetaPixelService } from 'src/app/modules/shared/services/meta-pixel/meta-pixel.service';
import { PanelService } from 'src/app/modules/shared/services/api/panel.service';
import { take } from 'rxjs/operators';
import { StateService } from 'src/app/modules/shared/services/storage/state.service';

@Component({
  selector: 'payment-box',
  templateUrl: './payment-box.component.html',
  styleUrls: ['./payment-box.component.scss']
})
export class PaymentBoxComponent implements OnInit, OnDestroy {
  priceOptions = { priceList: [], allowCustom: false };
  isSelectedPriceOutOfStock: boolean = false;
  isFixedPrice: boolean = false;

  @Input() product: Product;
  @Output() paymentBoxData = new EventEmitter<PaymentData>();

  readonly selectorDirections = {
    increase: 1,
    decrease: -1
  };

  customPlaceHolder;
  payablePrice = {
    price: 0,
    currency: ''
  };
  priceBeforeDiscount: number;
  exchangeCurrency;
  productVarieties: Variety[] = [];
  exchangeRates: Exchange[];
  subscriptions: Subscription[] = [];
  createOrderForm: FormGroup;
  deliverFormVariable = {
    deliveryType: 'yourself',
    receiveType: 'email',
    placeHolder: 'Email'
  };
  openCustomPriceSelector: boolean = false;
  cashBack;
  isServerSide: boolean = false;
  availableSKUs: VarietyAvailability[];
  customPriceSuggestion: PriceSuggestion[];
  rangedPriceSKU: string;
  rangedMinPrice: number;
  rangedMaxPrice: number;
  hasFixedPriceVariety: boolean;
  hasRangedPriceVariety: boolean;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private storeResolver: StoreDetailService,
    private exchangeResolver: ExchangeResolverService,
    private profileResolver: ProfileResolverService,
    private paymentService: PaymentService,
    private localStorageService: LocalStorageService,
    private googleTagManagerService: GoogleTagManagerService,
    private dataLayerService: DataLayerService,
    private metaPixelService: MetaPixelService,
    private panelService: PanelService,
    private stateService: StateService,
    public dialog: MatDialog,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isServerSide = isPlatformServer(platformId);
  }

  ngOnInit(): void {
    this.getProfileData();
    this.initOrderForm();
    this.getExchangeRates();

    if (!this.isServerSide) {
      this.googleTagManagerService.sendAnalytics('delivery_info', {
        method: 'email'
      });
    }
  }

  getProfileData() {
    if (!this.localStorageService.isLoggedIn) return;
    this.profileResolver.permissionInitInfo.subscribe((profileData) => {
      this.createOrderForm?.get('phone').patchValue(profileData?.email || '');
    });
  }

  initOrderForm() {
    this.createOrderForm = new FormGroup({
      price: new FormControl('', Validators.required),
      productCount: new FormControl(1, [
        Validators.required,
        Validators.min(1),
        Validators.max(5),
        Validators.pattern('^[0-9]*$')
      ]),
      customPrice: new FormControl(''),
      userDefinedCustomPrice: new FormControl(''),
      deliveryType: new FormControl('yourself', [Validators.required]),
      phone: new FormControl(''),
      name: new FormControl(''),
      note: new FormControl('')
    });
    this.handleFormUpdates();
  }

  handleFormUpdates() {
    const createFormSubscription = this.createOrderForm.valueChanges.subscribe(
      (value) => {
        this.handlePaymentData();
      }
    );
    this.subscriptions.push(createFormSubscription);

    const priceChangeSubscription = this.createOrderForm
      .get('price')
      .valueChanges.subscribe((price) => {
        this.calcPayablePrice();
      });
    this.subscriptions.push(priceChangeSubscription);

    const customPriceSubscription = this.createOrderForm
      .get('customPrice')
      .valueChanges.subscribe((customPrice) => {
        if (customPrice === 'custom') {
          // shows the custom input field
          this.openCustomPriceSelector = true;
          this.createOrderForm
            .get('userDefinedCustomPrice')
            .patchValue('', { emitEvent: false });
        } else {
          if (!this.openCustomPriceSelector) {
            this.createOrderForm
              .get('userDefinedCustomPrice')
              .patchValue(this.customPriceSuggestion[0].value, {
                emitEvent: false
              });
          }
          this.updatePriceSuggestions(+customPrice);
        }
        this.calcPayablePrice();
      });
    this.subscriptions.push(customPriceSubscription);

    const userDefinedCustomPriceSubscription = this.createOrderForm
      .get('userDefinedCustomPrice')
      .valueChanges.subscribe((price) => {
        if (this.createOrderForm.get('userDefinedCustomPrice').valid) {
          this.createOrderForm.get('customPrice').patchValue(+price);
          this.isSelectedPriceOutOfStock = false;
          this.stateService.setOutOfStockStatus(false);
        } else {
          this.payablePrice = {
            price: 0,
            currency: ''
          };
          this.priceBeforeDiscount = 0;
          this.isSelectedPriceOutOfStock = true;
          this.stateService.setOutOfStockStatus(this.isSelectedPriceOutOfStock);
        }
      });
    this.subscriptions.push(userDefinedCustomPriceSubscription);

    const orderFormSubscription = this.createOrderForm
      .get('deliveryType')
      .valueChanges.subscribe((deliveryType) => {
        this.deliverFormVariable.deliveryType = deliveryType;
      });
    this.subscriptions.push(orderFormSubscription);
  }

  handlePaymentData(navigateToCheckout?: boolean) {
    if (!this.exchangeCurrency) return;
    if (this.createOrderForm.invalid && navigateToCheckout) {
      return;
    }

    const isGift = this.createOrderForm.get('deliveryType').value === 'gift';
    const order: OrderData = {
      basePrice: this.getBasePrice(),
      exchangedPrice: this.getExchangedPrice(),
      sku: this.getSKU(),
      payablePrice: this.payablePrice.price,
      payablePriceWithoutDiscount: this.priceBeforeDiscount,
      orderCount: this.createOrderForm.get('productCount').value,
      exchangeRate: this.exchangeCurrency.rate,
      activationFee: this.getActivationFee()
    };
    const state: PaymentData = {
      order,
      product: {
        discount: +this.product.store_product_detail.discount,
        cashback: +this.product.store_product_detail.cashback,
        id: this.product.id,
        name: this.product.store_product_detail.name || this.product.name,
        categories: this.product.categories,

        logo: this.product.logo
      },
      currency: {
        code: this.exchangeCurrency.to_currency.code,
        symbol: this.exchangeCurrency.to_currency.symbol
      },
      country: { ...this.product.show_countries[0] },
      deliveryInfo: {
        email: this.createOrderForm.get('phone').value,
        ...(isGift && {
          giftInfo: {
            name: this.createOrderForm.get('name').value,
            note: this.createOrderForm.get('note').value
          }
        })
      },
      callbackUrl: `${window.location.origin}/payment`,
      isValid: this.createOrderForm.valid
    };
    this.emitPaymentBoxOutput(state);
    if (navigateToCheckout) {
      this.handlePaymentAnalytics();
      this.router.navigate(['/checkout']);
    }
  }

  getExchangeRates() {
    const exchangeResolver = this.exchangeResolver.permissionInit.subscribe(
      (data) => {
        this.exchangeRates = data;
        this.findRate();
      }
    );

    this.subscriptions.push(exchangeResolver);
  }

  private get priceQuery() {
    return this.route.snapshot.queryParamMap.get('fill');
  }

  selectPrice(selectedItem: PriceSuggestion) {
    if (!selectedItem) return;
    this.createOrderForm.get('customPrice')?.patchValue(selectedItem.value);
    this.updatePriceSuggestions(selectedItem.value);
  }

  private updatePriceSuggestions(selectedValue: number) {
    if (!this.customPriceSuggestion?.length) return;
    let isAnyActive = false;
    this.customPriceSuggestion.forEach((item) => {
      item.isActive = item.value === selectedValue;
      if (item.isActive) {
        isAnyActive = true;
      }
    });

    if (!isAnyActive && this.customPriceSuggestion.length > 0) {
      this.customPriceSuggestion[0].isActive = true;
    }
  }

  handlePriceSelector(direction: number) {
    const priceMin = Number(this.productVarieties[0]?.original_min_price) || 0;
    const currentPrice =
      this.customPriceInput === 'custom'
        ? priceMin
        : Number(this.createOrderForm.get('userDefinedCustomPrice').value);

    const nextPrice =
      this.customPriceInput === 'custom'
        ? priceMin
        : Math.max(
            Math.floor(
              currentPrice + this.productVarieties[0].price_step * direction
            ),
            priceMin
          );
    this.createOrderForm.get('userDefinedCustomPrice').patchValue(nextPrice);
  }

  findRate() {
    const { currency_detail } = this.storeResolver.permissionInit;
    let targetCurrency;
    let isEqualCurrency = true;
    if (currency_detail.iso_code == this.product.currency_detail.iso_code) {
      targetCurrency = {
        rate: 1,
        to_currency: {
          symbol: currency_detail.symbol,
          code: currency_detail.code
        }
      };
      isEqualCurrency = false;
    } else {
      targetCurrency = this.exchangeRates.find((el) => {
        isEqualCurrency = true;
        return (
          el.from_currency.code == this.product.currency_detail.code &&
          el.to_currency.code == currency_detail.code
        );
      });
    }

    this.exchangeCurrency = targetCurrency
      ? { ...targetCurrency, hasExchange: isEqualCurrency }
      : {
          rate: 1,
          to_currency: this.product.currency_detail,
          hasExchange: false
        };
    this.product.store_product_detail.store_varieties.forEach((variety) => {
      this.productVarieties.push(new Variety(variety, this.exchangeCurrency));
    });
    this.getProductAvailability(this.productVarieties.map((v) => v.sku));
  }

  get customPriceInput() {
    return this.createOrderForm.get('customPrice').value;
  }

  private setUserDefinedCustomPriceFields() {
    let variety = {
      min_price: this.productVarieties.find(
        (v) => v.sku === this.rangedPriceSKU
      ).original_min_price,
      max_price: this.productVarieties.find(
        (v) => v.sku === this.rangedPriceSKU
      ).original_max_price
    };
    this.customPlaceHolder = `${
      this.product?.currency_detail?.symbol
    }${Number(variety.min_price)} - ${
      this.product?.currency_detail?.symbol
    }${Number(variety.max_price)}`;

    this.createOrderForm
      .get('userDefinedCustomPrice')
      .setValidators([
        Validators.required,
        Validators.max(variety.max_price),
        Validators.min(variety.min_price),
        Validators.pattern('^[0-9]+(\\.[0-9]{1,2})?$')
      ]);
  }

  private getProductAvailability(varietySKUs: string[]) {
    this.panelService
      .checkStockAvailability(varietySKUs)
      .pipe(take(1))
      .subscribe((availabilities) => {
        this.availableSKUs = availabilities;
        this.hasFixedPriceVariety = this.productVarieties.some(
          (v) => v.min_price === v.max_price
        );
        const rangedVarieties = this.productVarieties.filter(
          (v) => v.min_price !== v.max_price && v.suggestions?.length > 0
        );
        this.hasRangedPriceVariety = rangedVarieties?.length > 0;
        this.rangedPriceSKU = this.productVarieties.find(
          (v) => v.min_price !== v.max_price
        )?.sku;
        if (this.hasFixedPriceVariety && this.hasRangedPriceVariety) {
          if (
            this.rangedPriceSKU &&
            this.availableSKUs.some((sku) => sku.sku === this.rangedPriceSKU)
          ) {
            this.isFixedPrice = false;
          } else {
            this.isFixedPrice = true;
          }
        } else if (this.hasFixedPriceVariety && !this.hasRangedPriceVariety) {
          this.isFixedPrice = true;
        } else if (!this.hasFixedPriceVariety && this.hasRangedPriceVariety) {
          this.isFixedPrice = false;
        }
        this.SelectFirstOption();
        this.updateAvailabilityStatus();
      });
  }

  private updateAvailabilityStatus(): void {
    if (!this.availableSKUs?.length) {
      this.isSelectedPriceOutOfStock = true;
      this.stateService.setOutOfStockStatus(this.isSelectedPriceOutOfStock);
      return;
    }
    if (!this.isFixedPrice) {
      const { super_immediate, prices, pre_order } =
        this.availableSKUs.find((sku) => sku.sku === this.rangedPriceSKU) || {};

      this.priceOptions.allowCustom = super_immediate === true;
      this.setUserDefinedCustomPriceFields();
      this.isSelectedPriceOutOfStock =
        !super_immediate && !pre_order && prices?.length === 0;
      this.stateService.setOutOfStockStatus(this.isSelectedPriceOutOfStock);

      return;
    }

    //availability check for fixed price
    const currentSKU: VarietyAvailability = this.availableSKUs.find(
      (sku) => sku.sku === this.createOrderForm.get('price')?.value?.sku
    );
    this.isSelectedPriceOutOfStock =
      !this.availableSKUs.some((sku) => sku.sku === currentSKU.sku) ||
      (!currentSKU.super_immediate &&
        !currentSKU.pre_order &&
        currentSKU.prices?.length === 0);
    this.stateService.setOutOfStockStatus(this.isSelectedPriceOutOfStock);
  }

  SelectFirstOption() {
    //when we have both fixed and ranged price varieties we will show the ranged price
    if (!this.isFixedPrice) {
      //if it's ranged
      this.createOrderForm.get('price').patchValue('Custom Price');
      this.setPriceSuggestions();
      this.setPriceByQueryParam('custom');
      if (
        this.priceOptions.allowCustom &&
        this.priceOptions.priceList.length === 0
      ) {
        this.createOrderForm.get('customPrice').setValue('custom');
      }
    } else if (this.isFixedPrice) {
      //check if it's fixed
      this.priceOptions.allowCustom = false;
      this.priceOptions.priceList = [
        ...this.productVarieties.map((v: Variety) => {
          return {
            label: `${this.product.currency_detail.symbol} ${parseFloat(v.min_price)}`,
            value: parseFloat(v.min_price),
            sku: v.sku,
            outOfStock:
              this.availableSKUs.length === 0 ||
              this.availableSKUs?.some(
                (sku) =>
                  sku?.sku === v.sku &&
                  !sku?.super_immediate &&
                  !sku?.pre_order &&
                  sku?.prices?.length === 0
              )
          };
        })
      ];
      const firstAvailableValue = this.priceOptions.priceList.find(
        (item) => !item.outOfStock
      );
      if (!firstAvailableValue) {
        this.isSelectedPriceOutOfStock = true;
        this.stateService.setOutOfStockStatus(this.isSelectedPriceOutOfStock);
        this.createOrderForm.get('price').patchValue(this.productVarieties[0]);
      } else {
        this.createOrderForm
          .get('price')
          .patchValue(
            this.productVarieties.find((v) => v.sku === firstAvailableValue.sku)
          );
      }

      this.stateService.setMinimumPrice(
        this.priceOptions.priceList[0].value,
        this.product.currency_detail.symbol
      );
      this.setPriceByQueryParam('select');
    }
    this.calcPayablePrice();
  }

  private setPriceSuggestions() {
    const availableSku: VarietyAvailability = this.availableSKUs.find(
      (sku) => sku.sku === this.rangedPriceSKU
    );
    this.rangedMinPrice = this.productVarieties.find(
      (v) => v.sku === availableSku.sku
    )?.original_min_price;
    this.rangedMaxPrice = this.productVarieties.find(
      (v) => v.sku === availableSku.sku
    )?.original_max_price;
    const suggestions = this.productVarieties[0].suggestions;
    const { symbol } = this.product.currency_detail;
    if (this.availableSKUs.length) {
      const prices = availableSku
        ? [
            ...availableSku.prices
              .map((price) => parseFloat(price.face_value))
              .sort((a, b) => a - b)
          ]
        : [];
      if (availableSku) {
        if (availableSku.super_immediate === true) {
          this.priceOptions.priceList = [...suggestions?.sort((a, b) => a - b)];
        } else if (availableSku.pre_order === true) {
          this.priceOptions.priceList = [...suggestions?.sort((a, b) => a - b)];
        } else if (prices.length) {
          this.priceOptions.priceList = [...prices];
        } else {
          this.priceOptions.priceList = [...suggestions?.sort((a, b) => a - b)];
        }
        if (this.priceOptions.priceList.length > 0) {
          this.stateService.setMinimumPrice(
            this.priceOptions.priceList[0],
            this.product.currency_detail.symbol
          );
        } else {
          if (availableSku) {
            this.stateService.setMinimumPrice(
              this.productVarieties.find((v) => v.sku === this.rangedPriceSKU)
                ?.min_price,
              this.product.currency_detail.symbol
            );
          } else {
            this.stateService.setMinimumPrice(undefined);
          }
        }
        const priceSuggestions = [
          ...this.priceOptions.priceList.map((value) => ({
            label: `${symbol}${value}`,
            value,
            isActive: false
          }))
        ];
        this.customPriceSuggestion = [...priceSuggestions];
        if (this.priceQuery) {
          this.updatePriceSuggestions(+this.priceQuery);
        } else {
          if (this.customPriceSuggestion.length > 0) {
            this.selectPrice(this.customPriceSuggestion[0]);
          }
        }
      }
    } else {
      this.priceOptions.priceList = [
        ...this.productVarieties.find((v) => v.sku === this.rangedPriceSKU)
          .suggestions
      ];
      if (this.priceOptions.priceList.length > 0) {
        this.stateService.setMinimumPrice(
          this.priceOptions.priceList[0],
          this.product.currency_detail.symbol
        );
      } else {
        this.stateService.setMinimumPrice(undefined);
      }
      const priceSuggestions = [
        ...this.priceOptions.priceList.map((value) => ({
          label: `${symbol}${value}`,
          value,
          isActive: false
        }))
      ];
      this.customPriceSuggestion = [...priceSuggestions];
      if (this.priceQuery) {
        this.updatePriceSuggestions(+this.priceQuery);
      } else {
        if (this.customPriceSuggestion.length > 0) {
          this.selectPrice(this.customPriceSuggestion[0]);
        }
      }
    }
  }

  customPriceOpened(isOpened: boolean) {
    if (
      isOpened &&
      this.priceOptions.allowCustom &&
      this.priceOptions.priceList.length != 0
    ) {
      this.openCustomPriceSelector = false;
    }
  }

  private setPriceByQueryParam(type: 'custom' | 'select') {
    const price = this.priceQuery;
    if (!price) return;

    if (type === 'custom') {
      this.createOrderForm.get('customPrice').setValue(+price);
    } else if (type === 'select') {
      const selectedItem = this.productVarieties.find(
        (item) => +item.original_max_price === +price
      );
      if (this.createOrderForm.get('price').value === selectedItem) {
        return;
      }
      if (selectedItem) {
        this.createOrderForm.get('price').setValue(selectedItem);
      }
    }
  }

  calcPayablePrice() {
    this.updateAvailabilityStatus();
    const itemPrice = !this.isFixedPrice
      ? this.createOrderForm.get('customPrice').value
      : this.createOrderForm.get('price').value?.min_price;

    const activationFee = !this.isFixedPrice
      ? +this.productVarieties[0]?.activation_fee
      : +this.createOrderForm.get('price').value?.activation_fee;
    const productPrice = this.paymentService.getPayableAmount({
      discount: +this.product?.store_product_detail.discount,
      itemPrice: +itemPrice,
      itemCount: 1,
      exchangeRate: this.exchangeCurrency.rate,
      activationFee: activationFee,
      share: 0
    }).payablePrice;
    this.priceBeforeDiscount = this.paymentService.getPayableAmount({
      discount: 0,
      itemPrice: +itemPrice,
      itemCount: 1,
      exchangeRate: this.exchangeCurrency.rate,
      activationFee: activationFee,
      share: 0
    }).payablePrice;

    if (!this.isFixedPrice && !this.createOrderForm.get('customPrice').valid) {
      this.payablePrice = {
        price: 0,
        currency: ''
      };
    } else {
      this.payablePrice = {
        price: productPrice,
        currency: this.exchangeCurrency.to_currency?.symbol
      };
    }
    if (this.product?.store_product_detail?.cashback) {
      this.cashBack =
        (Number(this.product.store_product_detail?.cashback) *
          this.payablePrice.price) /
        100;
    } else {
      this.cashBack = '';
    }
  }

  private emitPaymentBoxOutput(data: PaymentData) {
    this.paymentService.setPaymentData(data);
    this.paymentBoxData.emit(data);
  }

  private handlePaymentAnalytics() {
    if (!this.createOrderForm.valid) return;

    let analyticData = {
      currency: this.product?.currency_detail?.symbol,
      product: `${this.product.id}_${this.product.name}`,
      count: this.createOrderForm.get('productCount').value,
      price: `${this.product?.currency_detail?.symbol}${this.getBasePrice()}`,
      one_item_price: this.getExchangedPrice(),
      ...(!this.isFixedPrice && { price_range: this.customPlaceHolder })
    };

    this.googleTagManagerService.sendAnalytics(
      'selected_product_price_info',
      analyticData
    );
    this.googleTagManagerService.clearPreviousEcommerce();

    this.sendCartEvents({
      ...this.product,
      quantity: this.createOrderForm.get('productCount').value,
      price: this.getExchangedPrice(),
      payablePrice: this.payablePrice.price,
      currency: this.exchangeCurrency.to_currency.code
    });
  }

  private getExchangedPrice() {
    return !this.isFixedPrice
      ? Number(this.createOrderForm.get('customPrice').value) *
          Number(this.exchangeCurrency.rate)
      : this.createOrderForm.get('price').value?.min_price *
          Number(this.exchangeCurrency.rate);
  }

  private getBasePrice() {
    return !this.isFixedPrice
      ? this.createOrderForm.get('customPrice').value
      : this.createOrderForm.get('price').value?.original_min_price;
  }

  private getActivationFee() {
    return !this.isFixedPrice
      ? +this.productVarieties[0]?.activation_fee
      : +this.createOrderForm.get('price').value?.activation_fee;
  }

  private getSKU() {
    return !this.isFixedPrice
      ? this.productVarieties[0].sku
      : this.createOrderForm.get('price').value?.sku;
  }

  private sendCartEvents(data: any) {
    this.dataLayerService.pushGoogleEvent(
      GoogleEventTypes.BEGIN_CHECKOUT,
      data
    );
    this.dataLayerService.pushGoogleEvent(GoogleEventTypes.ADD_TO_CART, data);
    this.metaPixelService.pushEvent(FACEBOOK_EVENT_TYPES.ADD_TO_CART, {
      data: {
        content_ids: [`${this.product.id}`],
        contents: [
          {
            id: `${this.product.id}`,
            quantity: 1
          }
        ],
        content_type: 'product',
        currency: data.currency,
        value: data.payablePrice
      }
    });
  }

  ngOnDestroy(): void {
    this.stateService.setMinimumPrice(undefined);
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}

class Variety {
  id: number;
  activation_fee;
  title;
  min_price;
  max_price;
  original_min_price;
  original_max_price;
  sku;
  upc;
  currency;
  suggestions;
  price_step;

  constructor(variety, currency) {
    this.id = variety?.variety_detail?.id;
    this.activation_fee = variety?.activation_fee;
    this.title = variety?.variety_detail?.title;
    this.original_min_price = variety?.variety_detail?.min_price;
    this.original_max_price = variety?.variety_detail?.max_price;
    this.min_price = +variety?.variety_detail?.min_price;
    this.max_price = +variety?.variety_detail?.max_price;
    this.sku = variety?.variety_detail?.sku;
    this.upc = variety?.variety_detail?.upc;
    this.currency = currency.to_currency.symbol;
    this.suggestions = variety.suggestions;
    this.price_step = variety.variety_detail.price_step;
  }
}
